export const jiraNotesPackId = "jira-qa-notes-pack";

export const jiraPackIncludes = [
  "Detailed JIRA notes in a PDF-style learning format",
  "Step-by-step QA roadmap from basics to advanced usage",
  "JQL examples, workflow examples, and sprint tracking guidance",
  "Interview-ready answers for common JIRA questions",
];

export const jiraDeliveryFormats = [
  {
    title: "Structured PDF Notes",
    text: "A clean chapter-wise notes pack built for quick revision, self-study, and trainer-led guidance.",
  },
  {
    title: "Interview Q&A Section",
    text: "Ready answers for fresher and experienced QA interviews including priority vs severity and Scrum vs Kanban.",
  },
  {
    title: "Practical QA Templates",
    text: "Bug reporting points, lifecycle mapping, issue-linking examples, and JQL query references.",
  },
];

export const jiraRoadmapPhases = [
  {
    title: "Phase 1: JIRA Foundations",
    duration: "Week 1",
    topics: ["Introduction to JIRA", "Architecture and components", "Issue types", "Important issue fields"],
  },
  {
    title: "Phase 2: Daily QA Execution",
    duration: "Week 2",
    topics: ["Workflow and statuses", "Boards and backlog", "Sprint management", "Bug lifecycle and comments"],
  },
  {
    title: "Phase 3: Agile Delivery",
    duration: "Week 3",
    topics: ["Scrum in JIRA", "Epics and stories", "Linking issues", "Dashboards and reports"],
  },
  {
    title: "Phase 4: Power User Skills",
    duration: "Week 4",
    topics: ["JQL", "Filters", "Automation", "Integrations and APIs"],
  },
  {
    title: "Phase 5: Interview and Project Readiness",
    duration: "Week 5",
    topics: ["Real-time QA scenarios", "Best practices", "Advanced topics", "Interview questions and revision"],
  },
];

export const jiraModules = [
  {
    title: "1. Introduction to JIRA",
    answer:
      "JIRA is a project tracking and issue management tool from Atlassian used heavily in Agile teams. QA teams use it to log defects, follow sprint work, collaborate with developers, and maintain test-related evidence in one place. Compared with Bugzilla, Azure DevOps, and Trello, JIRA is usually stronger for workflow control, Agile boards, and reporting.",
    points: ["What JIRA is", "Why Agile teams use it", "JIRA vs Bugzilla, Azure DevOps, and Trello"],
  },
  {
    title: "2. JIRA Architecture and Components",
    answer:
      "The core building blocks are projects, issues, workflows, boards, dashboards, users, roles, and permissions. A project is the workspace, an issue is the item being tracked, a workflow controls status movement, and boards help teams plan and execute work visually.",
    points: ["Project", "Issue", "Workflow", "Boards, dashboards, roles, and permissions"],
  },
  {
    title: "3. Issue Types and QA Mapping",
    answer:
      "JIRA supports Bug, Task, Story, Epic, Sub-task, and Spike. In QA usage, a bug maps to a defect, a story maps to a requirement, and a task maps to a work item such as test execution, documentation, or environment validation.",
    points: ["Bug", "Task", "Story", "Epic", "Sub-task and Spike"],
  },
  {
    title: "4. Issue Fields That Matter",
    answer:
      "Summary tells the short problem statement, description explains the full context, priority shows business urgency, and severity reflects technical impact. Assignee, reporter, labels, components, fix version, environment, and attachments help teams route, reproduce, and verify work faster.",
    points: ["Priority vs severity explained clearly", "Environment and fix version usage", "Attachment and label discipline"],
  },
  {
    title: "5. Workflow and Status Management",
    answer:
      "A workflow defines how an issue moves from one status to another. Status is the current stage, while transition is the action that moves the issue. QA teams often follow a flow like Open, Assigned, In Progress, Retest, Closed, and Reopen so that defect ownership and verification remain clear.",
    points: ["To Do -> In Progress -> Done", "Custom workflows", "Conditions, validators, and post functions"],
  },
  {
    title: "6. Boards, Backlog, and Sprint Execution",
    answer:
      "Scrum boards support backlog, sprint planning, and sprint execution, while Kanban boards support continuous flow without sprint boundaries. QA teams use column mapping to align board columns with actual workflow statuses and track where stories and bugs are blocked.",
    points: ["Scrum board", "Kanban board", "Backlog and sprint tracking"],
  },
  {
    title: "7. Agile in JIRA",
    answer:
      "JIRA supports the full Scrum cycle including sprint planning, daily standup, review, and retrospective. QA uses this structure to understand scope, identify testing risks early, report blockers during standups, and validate done stories before sprint closure.",
    points: ["Sprint lifecycle", "Daily standup", "Review and retrospective"],
  },
  {
    title: "8. Epics, Stories, and Sprint Management",
    answer:
      "Epics group larger business goals, while stories break them into implementable requirements. Sprint management in JIRA includes creating the sprint, starting it, ending it, and carrying forward unfinished work. QA tracks bugs inside the same sprint and verifies completed stories before they are marked done.",
    points: ["Epic creation", "Story mapping", "Carry forward issues"],
  },
  {
    title: "9. JQL, Filters, and Dashboards",
    answer:
      "JQL helps testers search issues quickly using fields like project, status, assignee, priority, sprint, and labels. Saved filters make repeated queries reusable, and dashboards convert those filters into gadgets such as pie charts, burndown views, and issue statistics panels.",
    points: ['Example: project = QA AND status = "Open" AND priority = High', "Saved filters", "Dashboards and gadgets"],
  },
  {
    title: "10. Reports and Metrics",
    answer:
      "JIRA reports such as burndown, burnup, velocity, sprint report, and cumulative flow diagram help teams measure progress and identify delivery bottlenecks. QA can use these reports to see bug trends, incomplete work, and sprint health rather than only counting closed tickets.",
    points: ["Burndown chart", "Velocity chart", "Cumulative flow diagram"],
  },
  {
    title: "11. Bug Lifecycle and Real QA Scenarios",
    answer:
      "A practical QA bug lifecycle may move through New, Assigned, Open, Fixed, Retest, Closed, and Reopened. Real work includes raising a bug with steps, expected and actual results, linking it to a story, verifying the fix after deployment, and closing it only after confirmation in the correct environment.",
    points: ["Raise bug with evidence", "Link bug to story", "Retest and close correctly"],
  },
  {
    title: "12. Collaboration Features",
    answer:
      "Issue links such as Blocks, Depends On, Relates To, and Duplicate show cross-ticket relationships clearly. Comments, screenshots, logs, videos, watchers, and mentions improve collaboration and reduce repeated clarification during bug triage.",
    points: ["Issue linking", "Comments and tagging", "Notifications and watchers"],
  },
  {
    title: "13. User Management, Security, and Customization",
    answer:
      "Teams manage access using roles, permission schemes, and issue security levels. JIRA can also be customized with custom fields, workflows, screen schemes, and field configuration so that QA teams capture the exact details needed without overloading every issue with unnecessary inputs.",
    points: ["Roles and permissions", "Issue security", "Custom fields and workflows"],
  },
  {
    title: "14. Integrations, Automation, APIs, and Test Management",
    answer:
      "JIRA can connect with GitHub, GitLab, Jenkins, Selenium, Postman, and test management plugins like Xray and Zephyr. Teams often automate assignment or status updates, link commits to tickets, and use REST APIs to create or update issues programmatically for advanced reporting or internal tooling.",
    points: ["Git and CI/CD integration", "Rule-based automation", "REST API and test management plugins"],
  },
];

export const jiraInterviewAnswers = [
  {
    question: "What is the difference between Priority and Severity?",
    answer:
      "Priority shows how urgently the business wants the issue fixed. Severity shows how badly the defect affects the application. A cosmetic UI issue can have low severity but high priority if it affects a release demo.",
  },
  {
    question: "What is the difference between Epic, Story, and Task?",
    answer:
      "An epic is a large business goal, a story is a user-level requirement inside that goal, and a task is a smaller actionable work item such as testing, documentation, or setup.",
  },
  {
    question: "What is workflow in JIRA?",
    answer:
      "A workflow is the defined path of statuses and transitions that an issue follows from creation to closure.",
  },
  {
    question: "What is JQL?",
    answer:
      "JQL stands for JIRA Query Language. It is used to search and filter issues using project, status, sprint, assignee, labels, and many other fields.",
  },
  {
    question: "Scrum vs Kanban in JIRA?",
    answer:
      "Scrum uses time-boxed sprints and planning cycles. Kanban uses continuous flow and focuses on limiting work in progress without sprint boundaries.",
  },
];

export const jiraBonusSections = [
  "JQL cheat sheet for daily tester queries",
  "How to write a strong bug in JIRA with attachments",
  "Common mistakes while moving issues through workflow",
  "How QA should use dashboards during sprint execution",
];

export const jiraRealTimeExperience = [
  {
    title: "Before sprint starts",
    answer:
      "A QA engineer reviews upcoming stories in the backlog, checks acceptance criteria, asks requirement questions, estimates testing effort, identifies dependencies, and confirms which environments or test data will be needed before the sprint opens.",
  },
  {
    title: "During sprint execution",
    answer:
      "The tester tracks assigned stories on the board, updates issue status after execution, raises linked bugs with screenshots and logs, adds comments for blockers, joins daily standups with exact ticket numbers, and uses filters to avoid missing high-priority defects.",
  },
  {
    title: "During retest and regression",
    answer:
      "Once developers mark defects as fixed, QA moves the issues into retest, validates the patch in the correct environment, updates comments with proof, and decides whether the bug can be closed or must be reopened with fresh evidence.",
  },
  {
    title: "Before release",
    answer:
      "QA verifies unresolved defects, checks sprint report and dashboard trends, confirms linked stories are complete, tags release blockers, and shares sign-off status using JIRA filters, labels, fix versions, and burndown visibility.",
  },
];

export const jiraBugReportExample = {
  title: "Real-time QA bug example in JIRA",
  fields: [
    "Summary: Checkout button stays disabled after selecting valid UPI payment option",
    "Issue Type: Bug",
    "Priority: High",
    "Severity: Major",
    "Environment: UAT - Chrome 136 / Android 14",
    "Linked Story: PAYMENT-142",
    "Attachment: Screen recording + console log + screenshot",
  ],
  steps: [
    "Login with valid customer account",
    "Add one product to cart and proceed to checkout",
    "Select saved address and choose UPI payment",
    "Enter a valid UPI ID and wait for validation success",
    "Observe that the Place Order button remains disabled",
  ],
  expected:
    "After successful UPI validation, the Place Order button should be enabled so the user can complete payment.",
  actual:
    "UPI validation succeeds, but the Place Order button remains disabled and the order cannot be placed.",
  qaNote:
    "This is the kind of bug report MNC interviewers expect: reproducible steps, environment, business impact, linked requirement, and evidence.",
};

export const jiraMncInterviewQuestions = [
  {
    question: "How do you decide whether a defect should be reopened or a new defect should be created?",
    answer:
      "Reopen the original defect if the same root behavior is still failing or the fix was incomplete. Create a new defect when the issue is different in scope, affects a new area, or appears after the original fix as a separate regression. In interviews, explain that traceability and root-cause clarity matter more than only following habit.",
  },
  {
    question: "How would you use JIRA in a production defect situation?",
    answer:
      "Create or update a high-priority incident ticket immediately, capture timestamps and affected user flow, tag the correct team, attach logs or monitoring references, link it to the parent problem story if available, and keep comments factual. Then use watchers, labels, and dashboards so stakeholders can track resolution without asking for repeated manual updates.",
  },
  {
    question: "How do you handle a defect when the developer says, 'Cannot reproduce'?",
    answer:
      "I first recheck the exact build, environment, test data, and steps. I attach video proof, network logs, browser console output, and account details if safe to share. If needed, I discuss live with the developer and compare behavior against the linked story or acceptance criteria. In JIRA, I document every finding so the issue history stays objective and auditable.",
  },
  {
    question: "What dashboards would you create for a QA lead in a large organization?",
    answer:
      "I would create separate dashboard views for open defects by severity, sprint defects by assignee, blocker trend, reopen rate, unresolved release issues, and story-to-bug distribution. For leadership, keep the dashboard business-facing. For execution teams, keep it action-oriented with filters that directly support triage and follow-up.",
  },
  {
    question: "How does JIRA support traceability in enterprise projects?",
    answer:
      "Traceability improves when epics, stories, subtasks, test cases, defects, and releases are linked properly. A tester should connect bugs to the exact story or requirement, use fix versions and components consistently, and keep comments meaningful. In enterprise teams, this history becomes critical during audits, release reviews, and production investigations.",
  },
  {
    question: "How do you explain the difference between workflow status and board column in JIRA?",
    answer:
      "Status belongs to the workflow and represents the true state of an issue. Board columns are visual groupings that may map one or more statuses together. In enterprise projects, misunderstanding this causes reporting errors, so QA should always know the actual workflow status behind the board presentation.",
  },
  {
    question: "If you inherit a messy JIRA project, what would you improve first?",
    answer:
      "I would start with field hygiene, status clarity, issue type usage, and board mapping. After that I would clean duplicate labels, review permission gaps, simplify filters, and standardize defect templates. The goal is to make reporting trustworthy before adding more automation or dashboards.",
  },
  {
    question: "What JQL queries do you use most often as a senior QA or lead?",
    answer:
      "I regularly use queries for open blockers, sprint-wise unresolved defects, bugs assigned to me, reopened defects, issues without fix version, stories in testing status, and defects created after code freeze. In interviews, mention that the best JQL queries are the ones that support daily decisions, not just complex syntax.",
  },
];

export const jiraAdvancedJqlQueries = [
  'project = QA AND issuetype = Bug AND status in ("Open", "Reopened", "Retest") ORDER BY priority DESC, created DESC',
  'project = QA AND sprint in openSprints() AND assignee = currentUser() ORDER BY Rank ASC',
  'project = QA AND labels in (release-blocker) AND fixVersion = "Sprint 12 Release"',
  'project = QA AND issuetype = Bug AND created >= -7d ORDER BY severity DESC',
];

export const jiraIntroHighlights = [
  ["What is JIRA?", "Project management and issue tracking tool used widely in Agile teams."],
  ["Why QA uses JIRA?", "Bug tracking, story tracking, sprint visibility, and release coordination."],
  ["Who uses JIRA?", "QA, Developers, Scrum Masters, Product Owners, and Managers."],
  ["JIRA in Agile", "Sprint, backlog, board, story, bug, epic, and delivery visibility."],
  ["Real-time example", "Track an Amazon-style login or checkout defect from report to closure."],
];

export const jiraAgileBasics = [
  ["Epic", "Large feature or module"],
  ["Story", "User requirement"],
  ["Task", "Work item"],
  ["Bug", "Defect found by QA"],
  ["Sprint", "Fixed development and testing cycle"],
  ["Backlog", "Pending work list"],
  ["Scrum Board", "Active sprint board"],
  ["Kanban Board", "Continuous flow board"],
];

export const jiraEpicStoryExample = [
  ["Payment Module", "User should pay using UPI", "Create UPI UI", "UPI payment failing for PhonePe"],
];

export const jiraIssueTypesMatrix = [
  ["Epic", "Big module", "E-commerce checkout"],
  ["Story", "User requirement", "User should apply coupon"],
  ["Task", "Internal work", "Prepare test data"],
  ["Bug", "Defect", "Coupon discount not applying"],
  ["Sub-task", "Small part of task", "Verify coupon on mobile"],
  ["Improvement", "Enhancement", "Add filter in order page"],
];

export const jiraBugLifeCycleStatuses = [
  ["Open", "Bug created by QA"],
  ["To Do", "Pending action"],
  ["In Progress", "Developer working"],
  ["Fixed", "Developer fixed"],
  ["Ready for QA", "QA can retest"],
  ["Reopened", "Issue still exists"],
  ["Closed", "Bug verified and closed"],
  ["Rejected", "Not a valid bug"],
  ["Duplicate", "Already reported"],
  ["Deferred", "Will be fixed later"],
];

export const jiraBugCreationTemplate = [
  ["Project", "QACoder CRM"],
  ["Issue Type", "Bug"],
  ["Summary", "User is unable to submit enquiry form"],
  ["Description", "Submit button does not respond after entering valid details"],
  ["Steps to Reproduce", "1. Open enquiry page 2. Enter valid data 3. Click Submit"],
  ["Actual Result", "Nothing happens"],
  ["Expected Result", "Enquiry should be submitted successfully"],
  ["Severity", "High"],
  ["Priority", "P1"],
  ["Environment", "Chrome, Windows 11"],
  ["Attachment", "Screenshot / video"],
  ["Assignee", "Developer Name"],
  ["Reporter", "QA Name"],
];

export const jiraRealTimeBugExamples = [
  ["Amazon", "Product added to cart but cart count not updated", "Medium", "P2"],
  ["Flipkart", "Payment deducted but order not placed", "Critical", "P1"],
  ["Banking App", "User can login with expired OTP", "Critical", "P1"],
  ["Swiggy", "Coupon applied but discount not visible", "Medium", "P2"],
  ["QACoder CRM", "Demo enquiry not saved in database", "High", "P1"],
  ["LMS App", "Student can access paid course without payment", "Critical", "P1"],
];

export const jiraSeverityPriorityExamples = [
  ["High Severity, High Priority", "Critical issue, urgent fix", "Payment failed after deduction"],
  ["High Severity, Low Priority", "Serious but rare case", "Crash on old browser"],
  ["Low Severity, High Priority", "Minor issue but business urgent", "Company logo incorrect on homepage"],
  ["Low Severity, Low Priority", "Minor issue, not urgent", "Alignment issue in footer"],
];

export const jiraQaWorkflowSteps = [
  "Requirement received",
  "Story created in JIRA",
  "QA reviews acceptance criteria",
  "QA writes test cases",
  "Developer moves story to Ready for QA",
  "QA executes testing",
  "Bug created if issue found",
  "Developer fixes bug",
  "QA retests",
  "Bug closed",
  "Story marked Done",
];

export const jiraDashboardReports = [
  ["Sprint Report", "Sprint progress"],
  ["Burndown Chart", "Remaining work"],
  ["Velocity Chart", "Team capacity"],
  ["Bug Report", "Defect summary"],
  ["Created vs Resolved", "Bug trend"],
  ["Pie Chart", "Bugs by status or severity"],
  ["Filter Results", "Custom issue list"],
];

export const jiraJqlUseCases = [
  ["Find all bugs", "issuetype = Bug"],
  ["Find open bugs", "issuetype = Bug AND status = Open"],
  ["Find high priority bugs", "priority = High"],
  ["Find bugs assigned to me", "assignee = currentUser()"],
  ["Find bugs created by me", "reporter = currentUser()"],
  ["Find bugs in current sprint", "sprint in openSprints()"],
  ["Find reopened bugs", "status = Reopened"],
  ["Find bugs from project", "project = QACODER AND issuetype = Bug"],
];

export const jiraInterviewQuestionBank = [
  "What is JIRA?",
  "Why is JIRA used in testing?",
  "What is the difference between Epic, Story, Task, and Bug?",
  "What is a sprint?",
  "What is backlog?",
  "What is the bug life cycle?",
  "What is severity and priority?",
  "What is JQL?",
  "How do you create a bug in JIRA?",
  "What details should be added in a defect report?",
  "What is a dashboard in JIRA?",
  "What is a burndown chart?",
  "What is the difference between Scrum and Kanban board?",
  "What is reopened bug?",
  "What is deferred bug?",
];

export const jiraDetailedQaAnswers = [
  {
    question: "1. What is JIRA?",
    answer:
      "JIRA ek project management aur issue tracking tool hai jiska use software teams requirement tracking, bug tracking, sprint planning, backlog management, task assignment, defect lifecycle tracking aur reporting ke liye karti hain.",
    table: {
      headers: ["Use", "Meaning"],
      rows: [
        ["Bug Reporting", "Defect create karna"],
        ["Story Tracking", "Requirement ka status track karna"],
        ["Sprint Tracking", "Current sprint me kaam dekhna"],
        ["Backlog Management", "Pending requirements manage karna"],
        ["Defect Life Cycle", "Bug open se close tak track karna"],
        ["Reports", "Bug status, sprint progress, velocity"],
      ],
    },
    example:
      "Agar Amazon app me user payment karne ke baad order place nahi ho raha, QA JIRA me bug create karega with steps, actual result, expected result, severity, priority, screenshot ya video.",
  },
  {
    question: "2. What is Backlog in JIRA?",
    answer:
      "Backlog JIRA me pending work items ka collection hota hai jo future sprint ya future release ke liye planned hote hain.",
    table: {
      headers: ["Item", "Example"],
      rows: [
        ["Epic", "Payment Module"],
        ["User Story", "User should be able to pay using UPI"],
        ["Task", "Create payment gateway UI"],
        ["Bug", "Payment button not working"],
        ["Improvement", "Add wallet payment option"],
      ],
    },
    extraTable: {
      headers: ["Backlog Item", "Type"],
      rows: [
        ["Add student login", "User Story"],
        ["Add paid PDF download feature", "User Story"],
        ["Add Razorpay payment integration", "Task"],
        ["Fix newsletter email issue", "Bug"],
        ["Add course filter option", "Improvement"],
      ],
    },
  },
  {
    question: "3. What is Sprint Backlog?",
    answer:
      "Sprint backlog wo selected backlog items hote hain jo current sprint me complete karne ke liye team choose karti hai.",
    table: {
      headers: ["Product Backlog", "Sprint Backlog"],
      rows: [
        ["100 pending items", "Current 2 weeks ke selected items"],
        ["Future work", "Current sprint work"],
        ["Product Owner manage karta hai", "Scrum team execute karti hai"],
      ],
    },
    bullets: ["Product backlog me sab future items hote hain.", "Sprint backlog me sirf current sprint ke committed items hote hain."],
  },
  {
    question: "4. What is Priority in JIRA?",
    answer:
      "Priority batata hai ki bug ya task ko kitni jaldi fix karna hai. Priority business urgency ke basis par decide hoti hai.",
    table: {
      headers: ["Priority", "Meaning", "Example"],
      rows: [
        ["P1 / Critical", "Immediate fix required", "Payment not working"],
        ["P2 / High", "Important, fix soon", "Login not working for some users"],
        ["P3 / Medium", "Normal priority", "Search result sorting issue"],
        ["P4 / Low", "Can be fixed later", "Minor UI alignment issue"],
      ],
    },
    bullets: ["Priority usually Product Owner, Project Manager, BA ya client decide karte hain.", "QA priority suggest kar sakta hai, but final business decision alag ho sakta hai."],
  },
  {
    question: "5. What is Severity in JIRA?",
    answer:
      "Severity batata hai bug ka technical impact kitna serious hai. Ye system functionality par impact show karta hai.",
    table: {
      headers: ["Severity", "Meaning", "Example"],
      rows: [
        ["Blocker", "Testing ya deployment completely blocked", "Application not opening"],
        ["Critical", "Major business flow broken", "Payment failed after deduction"],
        ["Major", "Important feature broken", "User unable to update profile"],
        ["Minor", "Small functional or UI issue", "Button alignment issue"],
        ["Trivial", "Cosmetic issue", "Spelling mistake"],
      ],
    },
    bullets: ["Severity usually QA decide karta hai.", "Severity functional impact par based hoti hai, business urgency par nahi."],
  },
  {
    question: "6. Difference Between Priority and Severity",
    answer:
      "Priority bug ko kitni jaldi fix karna hai ye batata hai, jabki severity bug ka technical ya functional impact batati hai.",
    table: {
      headers: ["Basis", "Priority", "Severity"],
      rows: [
        ["Meaning", "Bug kitni jaldi fix hona chahiye", "Bug ka impact kitna serious hai"],
        ["Based on", "Business urgency", "Technical या functional impact"],
        ["Decided by", "PO, PM, Client", "QA, Tester"],
        ["Example", "Logo issue before launch = High priority", "Payment failure = High severity"],
      ],
    },
  },
  {
    question: "7-10. Severity and Priority Combination Examples",
    answer:
      "Interview me severity-priority examples bahut pooche jaate hain. Har combination ka real scenario yaad hona chahiye.",
    table: {
      headers: ["Case", "Scenario", "Severity", "Priority"],
      rows: [
        ["High Severity, High Priority", "Payment deducted but order not placed", "Critical", "P1 / High"],
        ["High Priority, Low Severity", "Wrong company logo before launch", "Low", "High"],
        ["High Severity, Low Priority", "Crash on unsupported old browser", "High", "Low"],
        ["Low Priority, Low Severity", "Footer spelling mistake", "Low", "Low"],
      ],
    },
    bullets: [
      "Payment failure business aur functionality dono impact karta hai.",
      "Wrong logo functionality break nahi karta but launch ke time urgent ho sakta hai.",
      "Unsupported browser crash technically serious ho sakta hai but business impact limited hota hai.",
      "Footer typo minor aur non-urgent hota hai.",
    ],
  },
  {
    question: "11. What is User Story in JIRA?",
    answer:
      "User story ek short requirement hoti hai jo user ke perspective se likhi jaati hai.",
    bullets: [
      "Standard format: As a [user type], I want [feature], So that [benefit].",
      "Example: As a student, I want to download paid API Testing PDF after successful payment, so that I can study API testing offline.",
    ],
    table: {
      headers: ["Field", "Meaning"],
      rows: [
        ["Summary", "Short title"],
        ["Description", "Requirement details"],
        ["Acceptance Criteria", "Conditions to mark story complete"],
        ["Story Points", "Effort estimation"],
        ["Priority", "Business importance"],
        ["Assignee", "Developer or owner"],
        ["Sprint", "Sprint name"],
        ["Epic Link", "Parent feature"],
      ],
    },
  },
  {
    question: "12. What are Acceptance Criteria?",
    answer:
      "Acceptance criteria wo conditions hoti hain jinhe fulfill karne ke baad user story complete ya done mani jaati hai.",
    table: {
      headers: ["No.", "Acceptance Criteria"],
      rows: [
        ["1", "Student should see paid PDF list"],
        ["2", "Student should see PDF price"],
        ["3", "Student should be able to click Buy Now"],
        ["4", "Payment page should open"],
        ["5", "After successful payment, PDF download link should be available"],
        ["6", "If payment fails, user should see proper error message"],
        ["7", "Free users should not access paid PDFs directly"],
      ],
    },
  },
  {
    question: "13-14. What are Story Points and Who Gives Them?",
    answer:
      "Story points Agile estimation unit hota hai jisse team effort, complexity aur risk estimate karti hai. Ye exact time measurement nahi hote.",
    table: {
      headers: ["Story Point", "Meaning"],
      rows: [
        ["1", "Very small work"],
        ["2", "Small work"],
        ["3", "Simple but needs some effort"],
        ["5", "Medium complexity"],
        ["8", "Complex"],
        ["13", "Very complex"],
        ["21", "Too big, should be broken into smaller stories"],
      ],
    },
    bullets: [
      "Common values: 1, 2, 3, 5, 8, 13, 21.",
      "Story points usually development team collectively decide karti hai.",
      "QA bhi discussion me participate karta hai because testing effort bhi complexity ka part hota hai.",
    ],
  },
  {
    question: "15-21. Root Cause, Steps to Reproduce, Actual Result, Expected Result, Summary and Description",
    answer:
      "Strong bug report ka core ye fields hote hain. Inhi se developer issue reproduce aur understand kar pata hai.",
    bullets: [
      "Root cause bug ka actual technical reason hota hai.",
      "Steps to reproduce clear, repeatable aur environment-specific hone chahiye.",
      "Actual result current wrong behavior hota hai.",
      "Expected result requirement ke according correct behavior hota hai.",
      "Summary short but clear title hota hai.",
      "Description detailed issue explanation hota hai.",
    ],
    example:
      "Example actual result: After clicking Submit, no confirmation message is displayed and form data is not saved. Example expected result: Enquiry should be submitted successfully and confirmation message should be displayed.",
  },
  {
    question: "22-31. Assignee, Reporter, Epic, Task, Sub-task and Bug States",
    answer:
      "Assignee current owner hota hai, reporter issue create karne wala person hota hai. Epic large feature hota hai, task work item hota hai, aur sub-task task ya story ka smaller piece hota hai.",
    bullets: [
      "Assignee example: Open me developer, Ready for QA me QA.",
      "Reporter example: Bug create karne wala QA.",
      "Epic example: Payment Module, Student Dashboard, Admin CRM.",
      "Task example: Configure SMTP, write test cases, create database table.",
      "Sub-task example: Create registration UI, add validation, perform QA testing.",
      "Rejected, Duplicate, Deferred aur Reopened statuses ko clearly samajhna zaroori hai.",
    ],
  },
];

export const jiraReadyMadeBugExamples = [
  {
    title: "Payment Bug",
    summary: "Payment successful but order is not created",
    steps: [
      "Login to application.",
      "Add product to cart.",
      "Proceed to checkout.",
      "Select UPI payment.",
      "Complete payment.",
      "Observe order confirmation.",
    ],
    actual: "Payment is deducted but order is not created.",
    expected: "Order should be created successfully after payment.",
    severity: "Critical",
    priority: "High",
    rootCause: "Order creation service failed after payment success callback.",
  },
  {
    title: "Login Bug",
    summary: "Valid user is unable to login",
    steps: ["Open login page.", "Enter valid email.", "Enter valid password.", "Click Login."],
    actual: "Invalid credentials message is displayed.",
    expected: "User should login successfully.",
    severity: "Critical",
    priority: "High",
    rootCause: "Authentication service is comparing wrong encrypted password hash.",
  },
  {
    title: "UI Bug",
    summary: "Submit button is not aligned properly on mobile view",
    steps: ["Open website on mobile.", "Navigate to enquiry page.", "Scroll to form section.", "Observe Submit button."],
    actual: "Submit button is partially outside the form container.",
    expected: "Submit button should be properly aligned inside form.",
    severity: "Low",
    priority: "Medium",
    rootCause: "Mobile responsive CSS media query missing for form button.",
  },
  {
    title: "API Bug",
    summary: "Create enquiry API returns 500 error for valid payload",
    steps: ["Open Postman.", "Select POST method.", "Enter endpoint /api/enquiries.", "Add valid request body.", "Click Send."],
    actual: "API returns 500 Internal Server Error.",
    expected: "API should return 201 Created.",
    severity: "High",
    priority: "High",
    rootCause: "Database column course_interest is not mapped correctly in backend entity.",
  },
  {
    title: "Security Bug",
    summary: "Normal student user can access admin lead list API",
    steps: ["Login as student.", "Copy student token.", "Call admin lead list API from Postman.", "Observe response."],
    actual: "API returns all lead details.",
    expected: "API should return 403 Forbidden.",
    severity: "Critical",
    priority: "High",
    rootCause: "Role-based authorization check is missing for admin API.",
  },
];

export const jiraPracticeAssignments = [
  {
    title: "Assignment 1: Create JIRA Bug",
    points: [
      "Create a bug for this scenario: On QACoder website, after student fills enquiry form and clicks Submit, no confirmation message is displayed.",
      "Student should write Summary, Description, Steps to Reproduce, Actual Result, Expected Result, Severity, Priority, Root Cause assumption, and whether attachment is required.",
    ],
  },
  {
    title: "Assignment 2: Identify Severity and Priority",
    table: {
      headers: ["Scenario", "Severity", "Priority"],
      rows: [
        ["Payment failed after amount deduction", "High", "High"],
        ["Wrong logo on homepage before launch", "Low", "High"],
        ["App crashes on unsupported browser", "High", "Low"],
        ["Spelling mistake in footer", "Low", "Low"],
        ["Login not working for all users", "High", "High"],
        ["Search filter not working", "Medium", "Medium or High"],
        ["Admin dashboard chart color wrong", "Low", "Low"],
        ["Student can access paid PDF without payment", "Critical", "High"],
        ["User profile image not uploading", "Medium", "Medium"],
        ["Contact page map not loading", "Low or Medium", "Low or Medium"],
      ],
    },
  },
];

export const jiraBestInterviewAnswers = [
  {
    title: "Best Interview Answer: Severity vs Priority",
    answer:
      "Severity defines the technical or functional impact of a defect, while priority defines how urgently the defect should be fixed from a business perspective. Usually QA decides severity and Product Owner ya business priority decide karte hain.",
  },
  {
    title: "Best Interview Answer: Bug Reporting in JIRA",
    answer:
      "When I find a bug, I first reproduce it 2-3 times to confirm. Then I create a JIRA bug with clear summary, detailed description, exact steps, actual result, expected result, environment details, test data, severity, priority, screenshot or logs. After developer fixes it, I retest and either close or reopen with evidence.",
  },
  {
    title: "Best Interview Answer: Story Points",
    answer:
      "Story points are used in Agile to estimate relative effort, complexity and risk. They are not exact hours. The team usually uses Fibonacci numbers like 1, 2, 3, 5, 8 and 13.",
  },
  {
    title: "Best Interview Answer: Backlog",
    answer:
      "Backlog is a list of pending work items such as user stories, bugs, tasks and improvements. Product backlog contains all future work, while sprint backlog contains selected items that the team commits to complete in the current sprint.",
  },
  {
    title: "Best Interview Answer: Root Cause",
    answer:
      "Root cause is the actual reason behind a defect. For example, if user is unable to login, the root cause may be incorrect password encryption logic, expired token validation issue, database connection issue, or wrong API response handling.",
  },
];

export const jiraFullTicketExample = {
  project: "QACoder Website",
  issueType: "Bug",
  summary: "Paid PDF download link is not generated after successful payment",
  description:
    "When student purchases API Testing paid PDF and completes payment successfully, the system does not generate the PDF download link. Payment is successful but student cannot access the purchased PDF.",
  preconditions: [
    "Student should be registered and logged in.",
    "Paid PDF should be available.",
    "Payment gateway should be active.",
  ],
  steps: [
    "Open QACoder website.",
    "Login as student.",
    "Navigate to Paid PDFs section.",
    "Select API Testing Zero to Hero PDF.",
    "Click on Buy Now.",
    "Complete payment using UPI or card.",
    "Observe the payment success page.",
    "Check whether download link is generated.",
  ],
  actual: "Payment is successful but PDF download link is not generated.",
  expected: "After successful payment, system should generate PDF download link and student should be able to download the purchased PDF.",
  severity: "Critical",
  priority: "High",
  environment: "Browser: Chrome, OS: Windows 11, Application URL: qacoder.in, Build: v1.0.0",
  testData: "Student Name: Sandhya, Course/PDF: API Testing Zero to Hero, Payment Method: UPI",
  attachment: "Payment success screenshot and console logs attached.",
  rootCause:
    "Payment success callback was received, but PDF access mapping was not inserted in student_purchase table due to missing transaction ID mapping.",
  developerComment:
    "Fixed transaction ID mapping issue and added PDF access entry after successful payment callback.",
  qaRetestComment:
    "Retested on build v1.0.1. PDF download link is generated successfully after payment. Closing the bug.",
  status: "Closed",
};

export const jiraGlossaryTerms = [
  ["Backlog", "Pending work list"],
  ["Sprint", "Fixed time-boxed development cycle"],
  ["Epic", "Large feature"],
  ["Story", "User requirement"],
  ["Task", "Work item"],
  ["Bug", "Defect"],
  ["Sub-task", "Small part of story or task"],
  ["Story Point", "Effort estimation"],
  ["Severity", "Impact of bug"],
  ["Priority", "Urgency of fix"],
  ["Assignee", "Person responsible"],
  ["Reporter", "Person who created issue"],
  ["RCA", "Root Cause Analysis"],
  ["JQL", "JIRA Query Language"],
  ["Workflow", "Status flow of issue"],
  ["Acceptance Criteria", "Conditions to complete story"],
  ["Definition of Done", "Final checklist before closing story"],
  ["Reopened", "Bug still exists after fix"],
  ["Deferred", "Postponed for future"],
  ["Duplicate", "Already reported issue"],
  ["Rejected", "Not valid bug"],
];
